import { ngFrameworkLoaded } from "@/shared/ng/ng-integration";
import { useNgSession } from "@/shared/ng/ng-session";
import { sazkaAlert } from "@/shared/composables/use-sazka-alert";
import { useSessionStorage } from "@/shared/composables/use-storage";
import { watch } from "vue";
import { Loader } from "@/shared/components/master-page/loader";
import analytics from "../analytics";

let chatLoaded = false;
let storage: ReturnType<typeof useSessionStorage>;

const loadChatProvider = () => {
    const promise = new Promise<void>((res, rej) => {
        if (chatLoaded) {
            res();
            return;
        }

        const scriptElement = document.createElement("script");
        scriptElement.type = "text/javascript";
        scriptElement.async = true;
        scriptElement.addEventListener("error", () => rej("Nepodařilo se načíst script chatu"), { passive: true });

        const referrer = document.referrer
            ? encodeURIComponent(document.referrer.substring(document.referrer.indexOf("://") + 1))
            : "";
        const location = window.location
            ? encodeURIComponent(window.location.href.substring(window.location.protocol.length))
            : "";

        const chatPath =
            "/index.php/cse/chat/getstatus/(click)/internal/(position)/api/(ma)/br/(top)/350/(units)/pixels/(leaveamessage)/true/(identifier)/prihlaseni/(survey)/1";
        scriptElement.src = `${environment.settings["Integrations:Chat:BaseAddress"]}${chatPath}?r="${referrer}&l=${location}`;
        document.body.appendChild(scriptElement);

        // periodically check, if chat is loaded
        const intervalHandle = setInterval(() => {
            if (typeof window.sce_instance !== "undefined") {
                clearInterval(intervalHandle);
                chatLoaded = true;
                res();
            }
        }, 50);
    });

    // do no add another watchers if chat is already loaded
    if (chatLoaded) return;

    promise.then(() => {
        const { session } = useNgSession();

        // notify player login status to chat provider
        watch(session, (newValue, oldValue) => {
            if (!sce_instance.notifySessionChange) return;
            if (newValue?.PlayerID == oldValue?.PlayerID) return; // there are changes in session, which should not trigger notification to chat provider

            const playerId = newValue?.PlayerID || oldValue?.PlayerID;
            sce_instance.notifySessionChange(
                playerId,
                newValue?.PlayerID ? "login" : "logout",
                session.value?.SessionToken || undefined,
            );
        });

        sce_instance.onClose(() => {
            storage.removeItem("chat-active");
        });
    });

    return promise;
};

const show = async (
    isProactiveChat = false,
    campaignCode?: string,
    creativeCode?: string,
    isComplaint?: boolean,
    complaintTicketNumber?: string,
) => {
    Loader.show();

    try {
        const promiseNg = ngFrameworkLoaded();
        const promiseChat = loadChatProvider();
        await Promise.all([promiseNg, promiseChat]);
    } catch (e) {
        sazkaAlert({
            title: "Chat je nedostupný",
            subText:
                "Omlouváme se, chat je momentálně nedostupný, kontaktujte nás prosím telefonicky na <a href='tel:266121212'>266 121 212</a>",
        });
        analytics.sendErrorEvent(
            "Omlouváme se, chat je momentálně nedostupný, kontaktujte nás prosím telefonicky na 266 121 212",
            "pop-up",
        );
        return;
    } finally {
        Loader.hide();
    }

    const { session, balanceNumeric, balance, isLoggedIn } = useNgSession();

    const player = {
        Brand: "SZK",
        Username: session.value?.PlayerEmail || "",
        PlayerID: session.value?.PlayerID || "",
        FirstName: session.value?.PlayerName || "",
        Gender: "", // we dont have the information
        Language: session.value?.PlayerLanguage || "CZE",
        Balance: (balanceNumeric.value / 100).toFixed(2) || "",
        Currency: balance.value?.Currency || "CZK",
        VipLevel: session.value?.PlayerVipLevel || "",
        PlayMode: "",
        BonusPolicyId: "",
        Depositor: isLoggedIn.value ? "Y" : "N",
        SessionKey: session.value?.SessionToken,
        flow: "",
        ProactiveChat: isProactiveChat ? "true" : undefined,
        campaignCode: campaignCode || undefined,
        creativeCode: creativeCode || undefined,
        isComplaint: isComplaint ? true : undefined,
        complaintTicketNumber: complaintTicketNumber || undefined,
    };

    //DEBUG Player Object:  console.log("show chat", player);
    sce_instance.showChat(player);
    storage.setItem("chat-active", "true", "essential");
};

const showComplaintChat = async (complaintTicketNumber: string) => {
    await show(false, undefined, undefined, true, complaintTicketNumber);
};

const registerChatToWindow = () => {
    window.showChat = () => show(false);
};

export const useChat = () => {
    storage = useSessionStorage();
    // follow up on active chat on reload
    if (storage.getItem("chat-active")) {
        loadChatProvider();
    }

    return {
        registerChatToWindow,
        show,
        showComplaintChat,
    };
};
